import { useIntl } from 'react-intl';
import moment from 'moment';
import { Link, useLocation } from '@tanstack/react-router';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Tooltip from '@mui/material/Tooltip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import DotIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { indigo, purple } from '@mui/material/colors';

import PublisherCard from '~/components/PublisherCard';
import Tag from '~/components/Tag';
import Spacings from '~/modules/ui-kit/spacings';
import type { Turn } from '~/hooks/api/useShift';
import messages from './messages';
import {
  getPublisherDisplayName,
  getColorForAssignmentState,
  getAssignmentStateLabel,
  getAssignmentStateDescription,
  type ShiftWithTurns,
} from './utils';

type ShiftCellProps = {
  shift: ShiftWithTurns;
  turns: Turn[];
  highlightPublisherId: number | null;
};

const getTurnLink = (shiftId: number, turnId: number, from?: string) => {
  const base = `/weekplan/assignment/${shiftId}/${turnId}`;
  return from ? `${base}?from=${encodeURIComponent(from)}` : base;
};

const ShiftCell = ({ shift, turns, highlightPublisherId }: ShiftCellProps) => {
  const intl = useIntl();
  const location = useLocation();

  const visibleTurns =
    highlightPublisherId != null
      ? turns.filter((turn) =>
          turn.turnAssignments.some(
            (a) => a.publisherMiniRecord?.id === highlightPublisherId
          )
        )
      : turns;

  if (visibleTurns.length === 0) return null;

  return (
    <Box sx={{ pt: 1 }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          whiteSpace: 'nowrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="subtitle2">
            {moment(shift.startDate).format('HH:mm')} –{' '}
            {moment(shift.endDate).format('HH:mm')}
          </Typography>
          {highlightPublisherId != null && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {intl.formatMessage(messages.applicationCount, {
                count: shift.unassignedApplicationCount ?? 0,
              })}
            </Typography>
          )}
        </Box>
        <Tooltip
          title={getAssignmentStateDescription(intl, shift.assignmentState)}
        >
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <DotIcon
              sx={{
                color: getColorForAssignmentState(shift.assignmentState),
                fontSize: 14,
              }}
            />
            <Typography variant="caption">
              {getAssignmentStateLabel(intl, shift.assignmentState)}
            </Typography>
          </Box>
        </Tooltip>
      </Box>
      <List disablePadding>
        {visibleTurns.map((turn) => (
          <Box key={turn.id}>
            <ListItemButton
              component={Link}
              to={getTurnLink(
                shift.id,
                turn.id,
                location.pathname + location.searchStr
              )}
              sx={{
                minHeight: 0,
                py: 0.5,
                typography: 'subtitle2',
                color: 'text.secondary',
                fontSize: '0.8125rem',
                bgcolor: 'action.selected',
                '&:hover': { bgcolor: 'action.focus' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <span>
                  {turn.route?.name ??
                    intl.formatMessage(messages.turnFallbackName, {
                      turnId: turn.id,
                    })}
                </span>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {turn.turnAssignments.length}
                </Typography>
              </Box>
            </ListItemButton>
            {[...turn.turnAssignments]
              .sort((a, b) => {
                const aFirst = a.publisherMiniRecord?.firstName ?? '';
                const bFirst = b.publisherMiniRecord?.firstName ?? '';
                const firstCmp = aFirst.localeCompare(bFirst);
                if (firstCmp !== 0) return firstCmp;
                const aLast = a.publisherMiniRecord?.lastName ?? '';
                const bLast = b.publisherMiniRecord?.lastName ?? '';
                return aLast.localeCompare(bLast);
              })
              .map((assignment) => {
                const record = assignment.publisherMiniRecord;
                if (!record) return null;
                const name = getPublisherDisplayName(record);
                const isHighlighted =
                  highlightPublisherId != null &&
                  record.id === highlightPublisherId;
                return (
                  <PublisherCard key={assignment.id} publisher={record}>
                    {(popoverProps) => (
                      <ListItemButton
                        dense
                        selected={isHighlighted}
                        sx={{
                          pl: 2,
                          '&.Mui-selected': {
                            bgcolor: purple[50],
                            color: purple[900],
                            fontWeight: 'bold',
                            '&:hover': { bgcolor: purple[100] },
                          },
                        }}
                        onClick={popoverProps.onClick}
                        aria-describedby={popoverProps['aria-describedby']}
                      >
                        <ListItemText
                          primary={
                            <Spacings.Inline scale="xs" alignItems="center">
                              <span>{name}</span>
                              {assignment.isLead && (
                                <Tag
                                  style={{
                                    backgroundColor: indigo[50],
                                    color: indigo[800],
                                    padding: '1px 6px',
                                    fontSize: '0.6875rem',
                                    lineHeight: 1,
                                  }}
                                  left={
                                    <CheckCircleIcon
                                      style={{
                                        height: '12px',
                                        width: 'auto',
                                        color: indigo[800],
                                      }}
                                    />
                                  }
                                >
                                  {intl.formatMessage(messages.teamLead)}
                                </Tag>
                              )}
                            </Spacings.Inline>
                          }
                        />
                      </ListItemButton>
                    )}
                  </PublisherCard>
                );
              })}
          </Box>
        ))}
      </List>
    </Box>
  );
};

export default ShiftCell;
